import React, { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { PAGE_ASPECT, pdfOptions } from "../pdfSetup";
import { PDF_URL } from "../lib/api";

const THUMB_W = 96;

function LazyThumb({ pageNumber, active, onJump }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { root: el.closest("[data-thumb-scroll]"), rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onJump(pageNumber)}
      data-testid={`thumb-page-${pageNumber}`}
      className="group flex flex-col items-center gap-1.5 focus:outline-none"
    >
      <div
        className={`relative overflow-hidden rounded-[2px] bg-[#FBFAF7] transition-all duration-300 ${
          active
            ? "ring-2 ring-gold shadow-[0_8px_20px_-8px_rgba(120,95,45,0.6)]"
            : "ring-1 ring-ivory-400 group-hover:ring-gold/60"
        }`}
        style={{ width: THUMB_W, height: Math.round(THUMB_W / PAGE_ASPECT) }}
      >
        {inView ? (
          <Page
            pageNumber={pageNumber}
            width={THUMB_W}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={<div className="h-full w-full bg-ivory-200" />}
          />
        ) : (
          <div className="h-full w-full bg-ivory-200" />
        )}
      </div>
      <span
        className={`text-[10px] tabular-nums ${active ? "text-gold-deep" : "text-ink-muted"}`}
      >
        {pageNumber}
      </span>
    </button>
  );
}

export const ThumbnailScrub = ({ open, onClose, pageCount, currentPage, onJump }) => {
  const [sliderVal, setSliderVal] = useState(currentPage);

  useEffect(() => {
    setSliderVal(currentPage);
  }, [currentPage, open]);

  const pct = pageCount > 1 ? ((sliderVal - 1) / (pageCount - 1)) * 100 : 0;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={onClose}
            data-testid="thumbnail-overlay"
          />
          <motion.div
            className="debossed fixed inset-x-0 bottom-0 z-50 rounded-t-2xl px-4 pb-6 pt-4 sm:px-8"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="thumbnail-panel"
          >
            <div className="mx-auto max-w-6xl">
              <div className="mb-3 flex items-center justify-between">
                <p className="kicker text-ink-soft">
                  Browse the book
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close browse panel"
                  data-testid="close-thumbnails-btn"
                  className="debossed-btn flex h-9 w-9 items-center justify-center rounded-full text-ink-soft"
                >
                  <X strokeWidth={1.4} className="h-4 w-4" />
                </button>
              </div>

              {/* Slider scrub */}
              <div className="mb-5 flex items-center gap-4">
                <input
                  type="range"
                  className="scrub w-full"
                  min={1}
                  max={pageCount}
                  value={sliderVal}
                  style={{ "--val": `${pct}%` }}
                  onChange={(e) => setSliderVal(Number(e.target.value))}
                  onMouseUp={(e) => onJump(Number(e.target.value))}
                  onTouchEnd={(e) => onJump(Number(e.target.value))}
                  onKeyUp={(e) => onJump(Number(e.target.value))}
                  data-testid="page-scrub-slider"
                  aria-label="Jump to page"
                />
                <span className="min-w-[70px] text-right font-display text-lg text-ink num-old">
                  {sliderVal}
                  <span className="text-ink-muted text-sm"> / {pageCount}</span>
                </span>
              </div>

              {/* Thumbnail rail */}
              <div
                data-thumb-scroll
                className="thin-scroll flex gap-3 overflow-x-auto pb-2"
                style={{ maxHeight: 210 }}
              >
                {open && (
                  <Document file={PDF_URL} options={pdfOptions} loading={null} error={null}>
                    <div className="flex gap-3">
                      {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                        <LazyThumb
                          key={p}
                          pageNumber={p}
                          active={p === currentPage}
                          onJump={onJump}
                        />
                      ))}
                    </div>
                  </Document>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
